import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageHeader } from '@/components/page-header';

// =====================================================================
// PageError — the ONE load-failure body for a panel screen (design audit
// leftovers, 2026-08-28).
//
// Before this, ~20 routes rendered a bare
//   <PageContainer><EmptyState title="Something went wrong" …/></PageContainer>
// when `/api/me` failed. The screen kept nothing: no <h1>, no eyebrow, no
// section — so a customer who hit a network blip could not tell WHICH page
// had failed, and the document lost its only level-1 heading. The page's
// identity is not data we have to fetch; it is a constant of the route, so
// it must survive the fetch failing.
//
// Composition, deliberately:
//   - PageHeader carries the SAME `eyebrow` + `title` the route uses in its
//     ready state, so the <h1> is byte-identical in both states.
//   - EmptyState is `compact` + `as="h2"` because it now sits UNDER a real
//     <h1> — a hero-size heading would shout over the page name, and a
//     second <h2>-less block would flatten the outline (empty-state.tsx's
//     own B8 note).
//   - `retry` renders an OUTLINE button. Never a filled one: `.impeccable.md`
//     allows one filled button per screen, and "try again" is a recovery
//     affordance, not the screen's primary action.
// =====================================================================

interface PageErrorProps {
  /** Section this page belongs to — must match the route's ready-state header. */
  eyebrow: string;
  /** The page's own name — must match the route's ready-state header. */
  title: string;
  /** What actually failed. Shown verbatim; the routes pass the fetch message. */
  message: string;
  /** Refetch for whatever failed. Omitted when the route has nothing to retry. */
  retry?: () => void;
}

export function PageError({ eyebrow, title, message, retry }: PageErrorProps) {
  return (
    <PageContainer>
      <PageHeader eyebrow={eyebrow} title={title} />
      {/* role="alert": the failure is announced when it appears, the way the
          shell's old bare <p role="alert"> was. */}
      <div role="alert">
      <EmptyState
        size="compact"
        as="h2"
        title="We couldn't load this page"
        description={message}
        action={
          retry ? (
            <Button type="button" variant="outline" onClick={retry}>
              Try again
            </Button>
          ) : undefined
        }
      />
      </div>
    </PageContainer>
  );
}
